
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Upload, Trash2, Image } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { useState, useEffect } from "react";

interface ImageUploadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadUrl: (url: string) => Promise<boolean>;
  onUploadFile: (file: File) => Promise<boolean>;
  onRemove: () => Promise<boolean>;
  currentImageUrl: string | null;
  isUploading: boolean;
}

export function ImageUploadDialog({
  isOpen,
  onClose,
  onUploadUrl,
  onUploadFile,
  onRemove,
  currentImageUrl,
  isUploading
}: ImageUploadDialogProps) {
  const [imageUrlInput, setImageUrlInput] = useState("");

  // Update input when dialog opens or currentImageUrl changes
  useEffect(() => {
    setImageUrlInput(currentImageUrl || "");
  }, [isOpen, currentImageUrl]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const success = await onUploadFile(file);
    if (success) onClose();
  };

  const handleImageUrlUpdate = async () => {
    const success = await onUploadUrl(imageUrlInput);
    if (success) onClose();
  };

  const handleImageRemove = async () => {
    const success = await onRemove();
    if (success) {
      setImageUrlInput(""); // Clear input after successful removal
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Update Fund Brand Image</DialogTitle>
          <DialogDescription>
            Upload a new image for this fund or provide an image URL.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Image URL</label>
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder="Enter image URL"
                value={imageUrlInput}
                onChange={(e) => setImageUrlInput(e.target.value)}
                disabled={isUploading}
              />
            </div>
            <Button 
              onClick={handleImageUrlUpdate}
              disabled={isUploading || !imageUrlInput.trim()}
              className="w-full"
            >
              {isUploading ? (
                <>
                  <Spinner size="sm" className="mr-2" ariaLabel="Updating" />
                  Updating...
                </>
              ) : (
                <>
                  <Image className="mr-2 h-4 w-4" />
                  Update from URL
                </>
              )}
            </Button>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                Or
              </span>
            </div>
          </div>

          <div className="space-y-4">
            <input
              type="file"
              accept="image/*"
              id="fund-image-upload"
              onChange={handleFileUpload}
              disabled={isUploading}
              className="hidden"
            />
            <label htmlFor="fund-image-upload">
              <Button 
                variant="outline" 
                className="w-full cursor-pointer" 
                disabled={isUploading}
                asChild
              >
                <div>
                  {isUploading ? (
                    <>
                      <Spinner size="sm" className="mr-2" ariaLabel="Uploading" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      Upload Image
                    </>
                  )}
                </div>
              </Button>
            </label>
          </div>

          {currentImageUrl && (
            <Button
              variant="destructive"
              onClick={handleImageRemove}
              disabled={isUploading}
              className="w-full"
            >
              {isUploading ? (
                <>
                  <Spinner size="sm" className="mr-2" ariaLabel="Removing" />
                  Removing...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Remove Image
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
